<script lang="ts">
  import { untrack } from 'svelte';
  import { Link } from 'svelte-routing';
  import { api } from '../../lib/api/client';
  import type { Activity, ActivityType } from '../../lib/api/types';
  import ImportList from '../../lib/components/imports/ImportList.svelte';
  import PageHeader from '../../lib/components/PageHeader.svelte';
  import { auth, currentUser, managedBy, type User } from '../../lib/stores/auth';
  import { units } from '../../lib/stores/units.svelte';
  import { GROUPS, SPORTS } from '../../lib/utils/sports';
  import { heightFields, heightFromFields, weightFields, weightFromFields } from '../../lib/utils/units';
  import { t } from '../../lib/i18n/index.ts';

  // Activities is the settings page for a member's activities: the details
  // their calories, zones, and step distance come from, automatic walks
  // (spec 006 FR-125), and everything that brings in old data: GPX, FIT, and
  // TCX files (spec 006 FR-124) and a Google Takeout archive (spec
  // 006 FR-114). A child in kids mode sees it read only, and a parent acting
  // for the child changes it here (spec 001 FR-096).
  let error = $state('');

  // setAutoWalks turns automatic walks on or off for every phone the member
  // uses (spec 006 FR-106).
  let autoWalksNote = $state('');
  async function setAutoWalks(on: boolean, box: HTMLInputElement) {
    try {
      auth.setUser(await api.patch<User>('/me', { auto_walks: on }));
      autoWalksNote = on ? t('settingsAutoWalksOn') : t('settingsAutoWalksOff');
      error = '';
    } catch (e: any) {
      box.checked = !on;
      error = e.message;
    }
  }

  // The member's activity details (spec 006 FR-125): body details for
  // calories and heart rate (spec 006 FR-049, 006 FR-055), entered in the
  // chosen units and sent in SI units (010 FR-034). Weight is one field, or stones and pounds; height is one
  // field, or feet and inches.
  let weight = $state(['', '']);
  let height = $state(['', '']);
  let birthYear = $state('');
  let sex = $state<'' | 'female' | 'male'>('');
  let maxHR = $state('');
  let bodyNote = $state('');
  let bodyLoaded = false;
  let weightUnit = $derived(units().weight);
  let heightUnit = $derived(units().height);
  // fieldUnits are the units the weight and height fields hold.
  let fieldUnits = { weight: units().weight, height: units().height };

  $effect(() => {
    const me = $currentUser;
    if (!me || bodyLoaded) return;
    bodyLoaded = true;
    untrack(() => {
      fieldUnits = { weight: weightUnit, height: heightUnit };
      weight = weightFields(me.weight_kg, weightUnit);
      height = heightFields(me.height_cm, heightUnit);
    });
    birthYear = me.birth_year ? String(me.birth_year) : '';
    sex = me.sex ?? '';
    maxHR = me.max_hr ? String(me.max_hr) : '';
  });

  // same is whether entry fields still hold what fields would show.
  const same = (have: string[], shown: [string, string]) => have[0].trim() === shown[0] && have[1].trim() === shown[1];

  // Changing units converts what the weight and height fields hold; fields
  // still showing the stored value are shown again from it, so switching
  // back and forth never drifts by rounding. Fields that are not numbers
  // are left as typed.
  $effect(() => {
    const unit = weightUnit;
    untrack(() => {
      if (!bodyLoaded || unit === fieldUnits.weight) return;
      const was = fieldUnits.weight;
      fieldUnits.weight = unit;
      const stored = $currentUser?.weight_kg;
      const kg = same(weight, weightFields(stored, was)) ? stored : weightFromFields(was, weight[0], weight[1]);
      if (kg != null) weight = weightFields(kg, unit);
    });
  });
  $effect(() => {
    const unit = heightUnit;
    untrack(() => {
      if (!bodyLoaded || unit === fieldUnits.height) return;
      const was = fieldUnits.height;
      fieldUnits.height = unit;
      const stored = $currentUser?.height_cm;
      const cm = same(height, heightFields(stored, was)) ? stored : heightFromFields(was, height[0], height[1]);
      if (cm != null) height = heightFields(cm, unit);
    });
  });

  // saveBody saves the details when a field changes, a text field once it
  // is left (010 FR-080). A weight or height left as shown is sent as
  // stored, so saving never rounds it.
  async function saveBody() {
    const me = $currentUser;
    const kg = same(weight, weightFields(me?.weight_kg, weightUnit)) ? (me?.weight_kg ?? 0) : weightFromFields(weightUnit, weight[0], weight[1]);
    const cm = same(height, heightFields(me?.height_cm, heightUnit)) ? (me?.height_cm ?? 0) : heightFromFields(heightUnit, height[0], height[1]);
    const y = birthYear.trim() ? Number(birthYear) : 0;
    // An empty maximum heart rate is sent as 0, which returns to the estimate from age.
    const hr = maxHR.trim() ? Number(maxHR) : 0;
    if (kg == null || cm == null || Number.isNaN(y) || Number.isNaN(hr)) {
      error = t('settingsEnterNumbers');
      return;
    }
    if (hr !== 0 && (hr < 100 || hr > 240)) {
      error = t('settingsMaxHrRange');
      return;
    }
    try {
      auth.setUser(await api.patch<User>('/me', { weight_kg: kg, height_cm: cm, birth_year: y, sex, max_hr: Math.round(hr) }));
      bodyNote = t('settingsSaved');
      error = '';
    } catch (err: any) {
      error = err.message;
    }
  }

  // importSports are the sports with a map, since an imported file carries a route.
  const importSports = SPORTS.filter((sp) => sp.gps);
  let importType = $state<ActivityType>('run');
  let importing = $state(false);
  let imported = $state<Activity[] | null>(null);
  let importError = $state('');

  // importFiles sends GPX, FIT, and TCX files from other apps, in forms of
  // up to 10 as the server takes them; each track, session, or activity
  // becomes an activity, of the sport its file names or else the one picked
  // here.
  async function importFiles(e: Event) {
    const input = e.currentTarget as HTMLInputElement;
    const files = [...(input.files ?? [])];
    input.value = '';
    if (!files.length) return;
    importing = true;
    importError = '';
    imported = null;
    const made: Activity[] = [];
    try {
      for (let at = 0; at < files.length; at += 10) {
        const form = new FormData();
        for (const f of files.slice(at, at + 10)) form.append('file', f);
        made.push(...(await api.upload<Activity[]>(`/me/activities/import?type=${importType}`, form, () => {})));
      }
    } catch (err: any) {
      importError = err.message;
    } finally {
      importing = false;
      if (made.length) imported = made;
    }
  }

  // around splits a message at the spot a link or styled word fills.
  const around = (message: string) => message.split('\u0000');
  const takeout = around(t('settingsImportGoogleHow', { site: '\u0000' }));
</script>

<PageHeader title={t('settingsActivitiesTitle')} level="section" />
{#if error}
  <div class="mb-4 p-3 rounded text-sm" style="background-color: var(--theme-error); color: var(--theme-base);" role="alert">{error}</div>
{/if}
{#if $managedBy}
  <p class="text-sm mb-4"><i aria-hidden="true" class="las la-lock mr-1"></i>{t('settingsManagedBy', { parent: $managedBy })}</p>
{/if}
<fieldset disabled={!!$managedBy} class="min-w-0 space-y-4">
{#if $currentUser}
  <section class="card p-5">
    <h2 class="font-semibold mb-2">{t('settingsYourDetails')}</h2>
    <p class="text-sm text-text-muted mb-3">{t('settingsDetailsAbout')}</p>
    <div class="flex flex-wrap items-end gap-3">
      {#if weightUnit === 'st'}
        <label class="text-sm">
          <span class="block mb-1">{t('settingsWeightSt')}</span>
          <input class="input w-20" inputmode="numeric" bind:value={weight[0]} oninput={() => (bodyNote = '')} onchange={saveBody} />
        </label>
        <label class="text-sm">
          <span class="block mb-1">{t('settingsAndLb')}</span>
          <input class="input w-20" inputmode="decimal" bind:value={weight[1]} oninput={() => (bodyNote = '')} onchange={saveBody} />
        </label>
      {:else}
        <label class="text-sm">
          <span class="block mb-1">{t('settingsWeightUnit', { unit: weightUnit })}</span>
          <input class="input w-28" inputmode="decimal" bind:value={weight[0]} oninput={() => (bodyNote = '')} onchange={saveBody} />
        </label>
      {/if}
      {#if heightUnit === 'ftin'}
        <label class="text-sm">
          <span class="block mb-1">{t('settingsHeightFt')}</span>
          <input class="input w-20" inputmode="numeric" bind:value={height[0]} oninput={() => (bodyNote = '')} onchange={saveBody} />
        </label>
        <label class="text-sm">
          <span class="block mb-1">{t('settingsAndIn')}</span>
          <input class="input w-20" inputmode="decimal" bind:value={height[1]} oninput={() => (bodyNote = '')} onchange={saveBody} />
        </label>
      {:else}
        <label class="text-sm">
          <span class="block mb-1">{t('settingsHeightCm')}</span>
          <input class="input w-28" inputmode="numeric" bind:value={height[0]} oninput={() => (bodyNote = '')} onchange={saveBody} />
        </label>
      {/if}
      <label class="text-sm">
        <span class="block mb-1">{t('settingsBirthYear')}</span>
        <input class="input w-28" inputmode="numeric" bind:value={birthYear} oninput={() => (bodyNote = '')} onchange={saveBody} />
      </label>
      <label class="text-sm">
        <span class="block mb-1">{t('settingsSex')}</span>
        <select class="input" bind:value={sex} onchange={() => { bodyNote = ''; saveBody(); }}>
          <option value="">{t('settingsSexNotSet')}</option>
          <option value="female">{t('settingsSexFemale')}</option>
          <option value="male">{t('settingsSexMale')}</option>
        </select>
      </label>
      <label class="text-sm">
        <span class="block mb-1">{t('settingsMaxHr')}</span>
        <input class="input w-28" inputmode="numeric" bind:value={maxHR} aria-describedby="max-hr-note" oninput={() => (bodyNote = '')} onchange={saveBody} />
      </label>
      {#if bodyNote}<span class="text-sm text-text-muted" aria-live="polite">{bodyNote}</span>{/if}
    </div>
    <p id="max-hr-note" class="text-xs text-text-muted mt-2">{t('settingsMaxHrNote')}</p>
    <label class="flex items-center gap-2 text-sm mt-4">
      <input type="checkbox" checked={!!$currentUser.auto_walks} onchange={(e) => setAutoWalks(e.currentTarget.checked, e.currentTarget)} />
      {t('settingsAutoWalks')}
    </label>
    <p class="text-sm text-text-muted mt-2">
      {t('settingsAutoWalksAbout')}
    </p>
    {#if autoWalksNote}<p class="text-sm text-text-muted mt-1">{autoWalksNote}</p>{/if}
  </section>
{/if}

<section class="card p-5 space-y-3">
  <h2 class="font-semibold">{t('settingsImportFiles')}</h2>
  <p class="text-sm text-text-muted">
    {t('settingsImportAbout')}
  </p>
  <div class="flex flex-wrap items-end gap-3">
    <label class="text-sm">
      <span class="block mb-1">{t('settingsImportSport')}</span>
      <select class="input" bind:value={importType}>
        {#each GROUPS as g (g.id)}
          <optgroup label={g.name}>
            {#each importSports.filter((sp) => sp.group === g.id) as sp (sp.id)}
              <option value={sp.id}>{sp.name}</option>
            {/each}
          </optgroup>
        {/each}
      </select>
    </label>
    <label class="btn btn-primary btn-sm cursor-pointer" class:opacity-50={!!$managedBy}>
      <i aria-hidden="true" class="las la-file-upload mr-1"></i>{importing ? t('settingsImporting') : t('settingsChooseFiles')}
      <input type="file" accept=".gpx,.fit,.tcx,application/gpx+xml" multiple class="hidden" disabled={importing} onchange={importFiles} />
    </label>
  </div>
  {#if importError}<p class="text-sm" style="color: var(--theme-error);" role="alert">{importError}</p>{/if}
  {#if imported}
    <p class="text-sm" aria-live="polite">
      {#if imported.length === 1}
        {@const added = around(t('settingsImportedOne', { activity: '\u0000' }))}
        {added[0]}<Link to={`/activity/${imported[0].id}`} class="underline">{imported[0].name}</Link>{added[1]}
      {:else}
        {@const added = around(t('settingsImportedMany', { count: imported.length, activities: '\u0000' }))}
        {added[0]}<Link to="/activities" class="underline">{t('settingsActivitiesTitle')}</Link>{added[1]}
      {/if}
    </p>
  {/if}
</section>

<ImportList title={t('settingsImportGoogle')} part="fit">
  <p>
    {takeout[0]}<span class="font-mono">takeout.google.com</span>{takeout[1]}
  </p>
  <p>
    {t('settingsImportGoogleWhat')}
  </p>
</ImportList>
</fieldset>
